import { useEffect, useRef, useState } from 'react';
import { FaBars, FaChevronRight, FaSignOutAlt, FaTimes, FaUser } from 'react-icons/fa';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import cicliiLogo from '../assets/ciclii3.png';
import { useAuth } from '../context/AuthContext';
import { routeMetadata, routes } from '../routes/routeConfig';
import Loader from './Loader';

const Dashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [learnMenuAnimating, setLearnMenuAnimating] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Extraer rutas privadas para el sidebar (excluyendo userProfile que está en el footer)
    // Agrupar subrutas bajo Aprendo sobre mí
    const sidebarRoutes = [
        routes.private.home,
        routes.private.calendar,
        {
            parent: routes.private.learnAboutMyself,
            children: [
                routes.private.knowYourCycle,
                routes.private.higiene,
                routes.private.mythsAndTruths,
                routes.private.tips,
            ],
        },
    ];

    const handleLogout = () => {
        if (isLoggingOut) {
            return;
        }

        setIsLoggingOut(true);

        logoutTimerRef.current = setTimeout(() => {
            logout();
            navigate(routes.redirects.afterLogout);
        }, 600);
    };

    useEffect(() => {
        return () => {
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
            }
        };
    }, []);

    const toggleSidebar = () => {
        if (sidebarOpen) {
            closeSidebar();
        } else {
            setSidebarOpen(true);
            setHeaderMenuOpen(true);
        }
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
        // Mantener la clase menu-open hasta que termine la animación de cierre
        setTimeout(() => {
            setHeaderMenuOpen(false);
        }, 400); // 400ms coincide con la duración de la animación
    };

    // Manejo de animación de cierre del submenú
    const handleToggleLearnMenu = () => {
        if (menuOpen) {
            setLearnMenuAnimating(true);
            setTimeout(() => {
                setMenuOpen(false);
                setLearnMenuAnimating(false);
            }, 300); // Duración igual a la animación CSS
        } else {
            setMenuOpen(true);
        }
    };

    return (
        <div className='dashboard-layout'>
            {isLoggingOut && (
                <div className='logout-overlay'>
                    <Loader text='Cerrando sesión...' />
                </div>
            )}
            {/* Mobile Header Bar */}
            <div className={`mobile-header-bar ${headerMenuOpen ? 'menu-open' : ''}`} onClick={sidebarOpen ? closeSidebar : toggleSidebar}>
                {sidebarOpen ? <FaTimes className='mobile-menu-icon' /> : <FaBars className='mobile-menu-icon' />}
                <div className='mobile-header-content'>
                    <img src={cicliiLogo} alt='CICLII Logo' style={{ maxHeight: 40, marginRight: 5 }} />
                    <span className='mobile-logo-text'>CICLII</span>
                </div>
            </div>

            {/* Overlay para cerrar sidebar en mobile */}
            <div
                className={`sidebar-overlay ${sidebarOpen ? 'overlay-active' : ''}`}
                onClick={closeSidebar}
            />

            {/* Sidebar */}
            <aside className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
                <div className='sidebar-header'>
                    <div className='sidebar-header-content'>
                        <img src={cicliiLogo} alt='CICLII Logo' style={{ maxHeight: 50 }} />
                        <span className='logo-text'>CICLII</span>
                    </div>
                </div>

                <nav className='sidebar-nav'>
                    {sidebarRoutes.map((item) => {
                        if (typeof item === 'string') {
                            const metadata = routeMetadata[item];
                            const IconComponent = metadata.icon;
                            return (
                                <NavLink
                                    key={item}
                                    to={item}
                                    className={({ isActive }) => `nav-item ${isActive ? 'nav-item-active' : ''}`}
                                    onClick={closeSidebar}
                                >
                                    <IconComponent className='nav-icon' />
                                    <span>{metadata.title}</span>
                                </NavLink>
                            );
                        } else if (item.parent && item.children) {
                            const parentMeta = routeMetadata[item.parent];
                            const ParentIcon = parentMeta.icon;
                            return (
                                <div className='nav-group' key={item.parent}>

                                    <div
                                        className={`nav-item nav-parent${menuOpen ? ' nav-parent-open' : ''}`}
                                        onClick={handleToggleLearnMenu}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <ParentIcon className='nav-icon' />
                                        <span>{parentMeta.title}</span>
                                        <span style={{ marginLeft: 'auto', fontSize: '1.1em' }}>
                                            <FaChevronRight style={{
                                                transition: 'transform 0.3s',
                                                transform: menuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                            }} />
                                        </span>
                                    </div>
                                    {menuOpen && (
                                        <div className={`nav-submenu ${learnMenuAnimating ? 'nav-submenu-fade-exit' : 'nav-submenu-fade'}`}>
                                            {item.children.map((subroute) => {
                                                const subMeta = routeMetadata[subroute];
                                                const SubIcon = subMeta.icon;
                                                return (
                                                    <NavLink
                                                        key={subroute}
                                                        to={subroute}
                                                        className={({ isActive }) => `nav-item nav-subitem ${isActive ? 'nav-item-active' : ''}`}
                                                        onClick={closeSidebar}
                                                    >
                                                        <SubIcon className='nav-icon' />
                                                        <span>{subMeta.title}</span>
                                                    </NavLink>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return null;
                    })}
                </nav>

                <div className='sidebar-footer'>
                    <div
                        className='user-info'
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            navigate(routes.private.userProfile);
                            closeSidebar();
                        }}
                    >
                        <FaUser className='user-icon' />
                        <div className='user-details'>
                            <span className='user-name'>{user?.nickname || 'Usuario'}</span>
                        </div>
                    </div>
                    <button onClick={handleLogout} className='btn-logout'>
                        <FaSignOutAlt />
                        <span>Cerrar Sesión</span>
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className='dashboard-main'>
                <Outlet />
            </main>
        </div>
    );
};

export default Dashboard;